import { useEffect, useState } from "react";
import { Building2, MapPin, Star, ShieldCheck, Phone, Mail, FileText, AlertTriangle, Loader2, Target } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { SectionFold, Field } from "./editor/SectionFold";
import { LEAD_PHASE_GROUPS, LEAD_SOURCES, LEAD_TITLES, PRICE_BRACKETS } from "../lib/enums";
import DatePickerInput from "./DatePickerInput";
import PlaceInput from "./PlaceInput";
import SearchableSelect from "./SearchableSelect";
import VoiceTextarea from "./VoiceTextarea";

/**
 * LeadEditor — the single canonical editor for both creating a new lead
 * and editing an existing one. Used:
 *   • inside the "New lead" modal on /leads
 *   • inline on /leads/:id (edit-in-place)
 *   • from any "Edit lead" button across the app
 *
 * Sections (collapsible, all expanded by default):
 *   • Identity & customer link
 *   • Site & location
 *   • Property metrics
 *   • Sales pulse (phase, source, rating, value, follow-up)
 *   • Notes
 *
 * On save, posts to /api/leads (create) or puts to /api/leads/{id} (edit),
 * then invokes onSaved(lead). The backend handles two-way sync into the
 * linked customer (industry / star_rating / contact details cascade).
 */

const INDUSTRIES = [
  "lodge", "hotel", "restaurant", "agriculture", "food_beverage",
  "industrial", "manufacturing", "mining", "retail", "office",
  "residential", "individual", "other",
];

const SECTIONS = [
  { key: "identity", title: "Identity & customer", icon: Building2, accent: "#1FB6C1" },
  { key: "location", title: "Site & location", icon: MapPin, accent: "#A78BFA" },
  { key: "property", title: "Property metrics", icon: Star, accent: "#F59E0B" },
  { key: "sales", title: "Sales pulse", icon: Target, accent: "#22C55E" },
  { key: "notes", title: "Notes", icon: FileText, accent: "#06B6D4" },
];

export default function LeadEditor({ value, onSaved, onCancel, defaultCustomerId }) {
  const isEdit = !!value?.id;
  const initial = {
    title: "",
    customer_id: defaultCustomerId || "",
    customer_name: "", customer_phone: "", customer_email: "",
    industry: "", province_area: "", estate_reserve: "",
    physical_address: "", lat: null, lng: null, place_id: "", address_source: "",
    star_rating: "", price_per_night: "", price_bracket: "",
    has_wtp: null, has_sla: null, needs_system: null,
    phase: "lead_logged",
    lead_source: "", lead_title_enum: "", lead_rating: "",
    current_provider: "",
    first_contact_date: "", next_followup_date: "",
    service_interest: "",
    notes: "",
    ...(value || {}),
  };
  const [form, setForm] = useState(initial);
  const [open, setOpen] = useState({ identity: true, location: true, property: true, sales: true, notes: true });
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const fold = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));

  // When picking an existing customer, prefill industry/contact/etc from
  // the customer record so the lead inherits their attributes.
  // We use ?? (nullish coalescing) instead of || so that explicit "" / 0
  // / false values from the customer still win over a stale form value.
  const onPickCustomer = (cid) => {
    const c = customers.find((x) => x.id === cid);
    if (c) {
      const pickValue = (cVal, fVal) => {
        if (cVal === null || cVal === undefined || cVal === "") return fVal;
        return cVal;
      };
      set({
        customer_id: cid,
        customer_name: c.name,
        customer_phone: pickValue(c.phone, form.customer_phone),
        customer_email: pickValue(c.email, form.customer_email),
        industry: pickValue(c.industry, form.industry),
        province_area: pickValue(c.province_area, form.province_area),
        estate_reserve: pickValue(c.estate_reserve, form.estate_reserve),
        star_rating: pickValue(c.star_rating, form.star_rating),
        price_per_night: pickValue(c.price_per_night, form.price_per_night),
        price_bracket: pickValue(c.price_bracket, form.price_bracket),
        has_wtp: c.has_wtp ?? form.has_wtp,
        has_sla: c.has_sla ?? form.has_sla,
        needs_system: c.needs_system ?? form.needs_system,
        current_provider: pickValue(c.current_provider, form.current_provider),
      });
    } else {
      set({ customer_id: "" });
    }
  };

  const submit = async () => {
    if (!form.title.trim()) { setErr("Title is required."); return; }
    setBusy(true); setErr("");
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([_, v]) => v !== "" && v !== null && v !== undefined),
      );
      // Coerce numerics
      if (payload.lead_rating) payload.lead_rating = Number(payload.lead_rating);
      if (payload.star_rating) payload.star_rating = Number(payload.star_rating);
      // Wave 19 #96 — coerce list-typed fields. Backend Pydantic v2 rejects
      // a raw string for List[...] with "Input should be a valid list".
      // The service-interest field is a comma-separated input by design; turn
      // it into an array here. Defensively coerce attachments too.
      if (typeof payload.service_interest === "string") {
        payload.service_interest = payload.service_interest
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        if (payload.service_interest.length === 0) {
          delete payload.service_interest;
        }
      }
      if (payload.attachments && !Array.isArray(payload.attachments)) {
        delete payload.attachments;
      }
      const r = isEdit
        ? await api.put(`/leads/${value.id}`, payload)
        : await api.post("/leads", payload);
      onSaved?.(r.data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not save"));
    } finally { setBusy(false); }
  };

  const TriBool = ({ field, label }) => (
    <Field label={label} half>
      <select
        value={form[field] === true ? "yes" : form[field] === false ? "no" : ""}
        onChange={(e) => {
          const v = e.target.value;
          set({ [field]: v === "yes" ? true : v === "no" ? false : null });
        }}
        className="hv-input mt-1"
        data-testid={`lead-edit-${field}`}
      >
        <option value="">—</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </Field>
  );

  return (
    <div className="space-y-2.5" data-testid="lead-editor">
      {err && (
        <div className="border border-rose-500/40 bg-rose-500/5 text-rose-300 text-xs p-2 rounded flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      <SectionFold section={SECTIONS[0]} open={open.identity} onToggle={() => fold("identity")} testIdPrefix="lead-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <Field label="Lead title *">
            <input value={form.title} onChange={(e) => set({ title: e.target.value })} className="hv-input mt-1" data-testid="lead-edit-title" autoFocus />
          </Field>
          <Field label="Lead category" half>
            <select value={form.lead_title_enum} onChange={(e) => set({ lead_title_enum: e.target.value })} className="hv-input mt-1" data-testid="lead-edit-title-enum">
              <option value="">—</option>
              {LEAD_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Existing customer (auto-prefills below)" half>
            <SearchableSelect
              value={form.customer_id || ""}
              onChange={onPickCustomer}
              options={customers.map((c) => ({
                id: c.id,
                label: c.name,
                sublabel: [c.external_ref && `Acc# ${c.external_ref}`, c.vat_number && `VAT ${c.vat_number}`, c.phone, c.email].filter(Boolean).join(" · "),
                searchTerms: [c.name, c.external_ref, c.vat_number, c.phone, c.whatsapp, c.email, c.industry, c.physical_address, c.legal_name, c.short_id].filter(Boolean),
              }))}
              placeholder="— None / new prospect —"
              testId="lead-edit-customer"
              emptyHint="No customers match. Try VAT, account no., phone, or email."
            />
          </Field>
          {!form.customer_id && (
            <Field label="Prospect / lodge name" half>
              <input value={form.customer_name} onChange={(e) => set({ customer_name: e.target.value })} className="hv-input mt-1" data-testid="lead-edit-customer-name" />
            </Field>
          )}
          <Field label="Phone" half>
            <div className="relative mt-1">
              <Phone className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#647087]" />
              <input value={form.customer_phone} onChange={(e) => set({ customer_phone: e.target.value })} className="hv-input pl-8" data-testid="lead-edit-phone" />
            </div>
          </Field>
          <Field label="Email" half>
            <div className="relative mt-1">
              <Mail className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#647087]" />
              <input value={form.customer_email} onChange={(e) => set({ customer_email: e.target.value })} className="hv-input pl-8" data-testid="lead-edit-email" />
            </div>
          </Field>
        </div>
      </SectionFold>

      <SectionFold section={SECTIONS[1]} open={open.location} onToggle={() => fold("location")} testIdPrefix="lead-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <Field label="Industry" half>
            <select value={form.industry} onChange={(e) => set({ industry: e.target.value })} className="hv-input mt-1" data-testid="lead-edit-industry">
              <option value="">—</option>
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </Field>
          <Field label="Province / area" half>
            <input value={form.province_area} onChange={(e) => set({ province_area: e.target.value })} className="hv-input mt-1" data-testid="lead-edit-province" />
          </Field>
          <Field label="Estate / reserve">
            <input value={form.estate_reserve} onChange={(e) => set({ estate_reserve: e.target.value })} className="hv-input mt-1" data-testid="lead-edit-estate" />
          </Field>
          <Field label="Verified address (Google) — required for trip planning">
            <PlaceInput
              value={form.physical_address ? {
                address: form.physical_address,
                lat: form.lat,
                lng: form.lng,
                place_id: form.place_id,
                source: form.address_source || (form.lat && form.lng ? "legacy" : "manual"),
              } : null}
              onChange={(v) => set({
                physical_address: v?.address || "",
                lat: v?.lat ?? null,
                lng: v?.lng ?? null,
                place_id: v?.place_id || "",
                address_source: v?.source || "",
              })}
              placeholder="Search the lodge / business / address…"
              testId="lead-edit-place"
            />
          </Field>
        </div>
      </SectionFold>

      <SectionFold section={SECTIONS[2]} open={open.property} onToggle={() => fold("property")} testIdPrefix="lead-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <Field label="Star rating (1-5)" half>
            <input type="number" min="0" max="6" step="0.5" value={form.star_rating ?? ""} onChange={(e) => set({ star_rating: e.target.value })} className="hv-input mt-1" data-testid="lead-edit-star-rating" />
          </Field>
          <Field label="Price per night" half>
            <input value={form.price_per_night} onChange={(e) => set({ price_per_night: e.target.value })} className="hv-input mt-1" data-testid="lead-edit-price-night" placeholder="R20,000-up" />
          </Field>
          <Field label="Price bracket (ZAR)" half>
            <select value={form.price_bracket} onChange={(e) => set({ price_bracket: e.target.value })} className="hv-input mt-1" data-testid="lead-edit-price-bracket">
              <option value="">—</option>
              {PRICE_BRACKETS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
            </select>
          </Field>
          <TriBool field="has_wtp" label="Has WTP?" />
          <TriBool field="has_sla" label="Has SLA?" />
          <TriBool field="needs_system" label="Needs system?" />
        </div>
      </SectionFold>

      <SectionFold section={SECTIONS[3]} open={open.sales} onToggle={() => fold("sales")} testIdPrefix="lead-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <Field label="Phase" half>
            <select value={form.phase} onChange={(e) => set({ phase: e.target.value })} className="hv-input mt-1" data-testid="lead-edit-phase">
              {LEAD_PHASE_GROUPS.map((g) => (
                <optgroup key={g.group} label={g.group}>
                  {g.phases.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </optgroup>
              ))}
            </select>
          </Field>
          <Field label="Source / entry method" half>
            <select value={form.lead_source} onChange={(e) => set({ lead_source: e.target.value })} className="hv-input mt-1" data-testid="lead-edit-source">
              <option value="">—</option>
              {LEAD_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Lead rating (1-5)" half>
            <select value={form.lead_rating} onChange={(e) => set({ lead_rating: e.target.value })} className="hv-input mt-1" data-testid="lead-edit-rating">
              <option value="">—</option>
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{`${"★".repeat(n)} (${n})`}</option>)}
            </select>
          </Field>
          <Field label="Current provider" half>
            <input value={form.current_provider} onChange={(e) => set({ current_provider: e.target.value })} className="hv-input mt-1" data-testid="lead-edit-current-provider" />
          </Field>
          <Field label="First contact" half>
            <DatePickerInput value={(form.first_contact_date || "").slice(0, 10)} onChange={(v) => set({ first_contact_date: v })} testId="lead-edit-first-contact" />
          </Field>
          <Field label="Next follow-up" half>
            <DatePickerInput value={(form.next_followup_date || "").slice(0, 10)} onChange={(v) => set({ next_followup_date: v })} testId="lead-edit-next-followup" />
          </Field>
          <Field label="Service interest (comma sep.)">
            <input value={form.service_interest} onChange={(e) => set({ service_interest: e.target.value })} className="hv-input mt-1" data-testid="lead-edit-service-interest" placeholder="RO, Filtration, Maintenance" />
          </Field>
        </div>
      </SectionFold>

      <SectionFold section={SECTIONS[4]} open={open.notes} onToggle={() => fold("notes")} testIdPrefix="lead-section">
        <Field label="Notes">
          <VoiceTextarea
            value={form.notes}
            onChange={(v) => set({ notes: v })}
            rows={5}
            className="mt-1"
            context="lead_activity"
            testIdPrefix="lead-edit-notes-voice"
            data-testid="lead-edit-notes"
          />
        </Field>
        {form.industry === "lodge" && (
          <div className="text-[10px] text-[#647087] flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3 text-[#22C55E]" /> Lodge metrics will sync back to the customer record on save.
          </div>
        )}
      </SectionFold>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && <button type="button" onClick={onCancel} className="hv-btn hv-btn-ghost" data-testid="lead-edit-cancel">Cancel</button>}
        <button type="button" onClick={submit} disabled={busy} className="hv-btn hv-btn-primary" data-testid="lead-edit-submit">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {isEdit ? "Save changes" : "Create lead"}
        </button>
      </div>
    </div>
  );
}
